import { WindowMessenger } from "penpal";
import type { Methods } from "penpal";

/**
 * A factory that produces a penpal channel descriptor.
 *
 * The two phantom type parameters are never present at runtime – they exist
 * only so that `connect` can build a fully-typed channels record without
 * any manual type assertions at the call site.
 *
 *   TChannel      – the literal channel name (e.g. "debug")
 *   TRemoteMethods – the methods the OTHER side exposes (what callers receive)
 */
export type ChannelFactory<
  TChannel extends string = string,
  TRemoteMethods extends Methods = Methods,
> = (messenger: WindowMessenger) => {
  channel: TChannel;
  messenger: WindowMessenger;
  methods: Methods;
  /** Phantom – typed but never set at runtime. Do not access. */
  readonly _remoteMethods: TRemoteMethods;
};

export function createChannelDescriptor<
  TDomain extends "host" | "remote",
  TChannel extends string,
  THostMethods extends Methods,
  TRemoteMethods extends Methods,
>(
  domain: TDomain,
  channel: TChannel,
  config: {
    hostMethods?: () => THostMethods;
    remoteMethods?: () => TRemoteMethods;
  },
): ChannelFactory<
  TChannel,
  TDomain extends "host" ? TRemoteMethods : THostMethods
> {
  const resolvedMethods =
    domain === "host" ? config.hostMethods?.() : config.remoteMethods?.();
  return ((messenger: WindowMessenger) => ({
    channel,
    messenger,
    methods: resolvedMethods,
  })) as unknown as ChannelFactory<
    TChannel,
    TDomain extends "host" ? TRemoteMethods : THostMethods
  >;
}
